import React, { useMemo, useState } from 'react';
import {
  FileText, Download, Clock, CheckCircle, AlertCircle, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Line, Scatter, CartesianGrid, XAxis, YAxis,
  Tooltip, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';

const STATUS_CFG = {
  in_progress: { label: 'In Progress', icon: Clock,      color: 'text-amber-600',   bg: 'bg-amber-100 dark:bg-amber-900/30' },
  submitted:   { label: 'Submitted',   icon: FileText,    color: 'text-blue-600',    bg: 'bg-blue-100 dark:bg-blue-900/30' },
  evaluated:   { label: 'Evaluated',   icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  not_started: { label: 'Not Started', icon: AlertCircle, color: 'text-slate-500',   bg: 'bg-slate-100 dark:bg-slate-700' },
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmtAxisDate = (ts) => new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function ProgressTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg dark:border-slate-700 dark:bg-slate-800">
      <p className="font-semibold text-slate-900 dark:text-white">{point.title}</p>
      <p className="text-slate-500 dark:text-slate-400">{point.subject} &middot; {fmtDate(point.date)}</p>
      {point.type === 'absent' ? (
        <p className="mt-1 font-medium text-rose-600 dark:text-rose-400">Not attempted</p>
      ) : (
        <p className="mt-1 font-medium text-primary dark:text-accent-cool">
          {point.raw}/{point.total} ({point.percentage.toFixed(1)}%)
        </p>
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tone || 'text-slate-900 dark:text-white'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">{sub}</p>}
    </div>
  );
}

export default function StudentProgressDashboard({
  data,
  loading,
  onDownloadSubmission,
  onDownloadCorrected,
  // Students get a view-only watermarked viewer instead of a download.
  renderCorrectedPaper,
  historyLabel = 'Exam History',
  emptyHistoryMessage = 'No exam attempts yet.',
}) {
  const { isDark } = useTheme();
  const [subjectFilter, setSubjectFilter] = useState('all');

  const student = data?.student;
  // Each ?? fallback allocates a fresh [] / {} on every render, which would make
  // every downstream useMemo below recompute every time (and is what the
  // exhaustive-deps warnings were pointing at). Memoising the fallbacks gives
  // them stable identities; behaviour is unchanged.
  const attempts = useMemo(() => data?.attempts ?? [], [data?.attempts]);
  const unattempted = useMemo(() => data?.unattempted ?? [], [data?.unattempted]);
  const attendance = useMemo(() => data?.attendance ?? [], [data?.attendance]);
  // Offline (paper) tests with no result yet are Pending: not plotted, not Missed.
  const missed = useMemo(() => unattempted.filter((u) => u.offline_state !== 'pending'), [unattempted]);
  const homeworkSummary = useMemo(
    () => data?.homework_summary ?? { done: 0, not_done: 0, pending: 0 },
    [data?.homework_summary]
  );

  const lineColor = isDark ? '#5c7e8b' : '#04405c';
  const gridColor = isDark ? '#334155' : '#e2e8f0';
  const tickColor = isDark ? '#94a3b8' : '#64748b';
  const absentColor = '#f43f5e';
  const presentColor = '#10b981';
  const pendingColor = '#94a3b8';

  const subjects = useMemo(() => {
    const set = new Set();
    attempts.forEach((a) => a.exam_subject && set.add(a.exam_subject));
    unattempted.forEach((u) => u.exam_subject && set.add(u.exam_subject));
    return Array.from(set).sort();
  }, [attempts, unattempted]);

  const evaluatedAttempts = useMemo(
    () => attempts.filter((a) => a.status === 'evaluated' && a.total_marks),
    [attempts]
  );

  const scorePoints = useMemo(() => {
    return evaluatedAttempts
      .filter((a) => subjectFilter === 'all' || a.exam_subject === subjectFilter)
      .map((a) => ({
        x: new Date(a.attempted_at).getTime(),
        date: a.attempted_at,
        subject: a.exam_subject,
        title: a.exam_title,
        percentage: (a.total_score / a.total_marks) * 100,
        raw: a.total_score,
        total: a.total_marks,
        type: 'score',
      }))
      .sort((a, b) => a.x - b.x);
  }, [evaluatedAttempts, subjectFilter]);

  const absentPoints = useMemo(() => {
    return missed
      .filter((u) => subjectFilter === 'all' || u.exam_subject === subjectFilter)
      .map((u) => ({
        x: new Date(u.exam_date || u.assigned_at).getTime(),
        date: u.exam_date || u.assigned_at,
        subject: u.exam_subject,
        title: u.exam_title,
        percentage: 0,
        type: 'absent',
      }))
      .sort((a, b) => a.x - b.x);
  }, [missed, subjectFilter]);

  const summary = useMemo(() => {
    const pcts = evaluatedAttempts.map((a) => (a.total_score / a.total_marks) * 100);
    const average = pcts.length ? pcts.reduce((s, v) => s + v, 0) / pcts.length : null;
    const lastPct = pcts.length ? pcts[0] : null; // attempts already ordered desc by date
    const prevPct = pcts.length > 1 ? pcts[1] : null;
    const trend = lastPct != null && prevPct != null ? lastPct - prevPct : null;

    const presentCount = attendance.filter((r) => r.status === 'present').length;
    const attendancePct = attendance.length ? (presentCount / attendance.length) * 100 : null;

    return { average, lastPct, trend, attendancePct };
  }, [evaluatedAttempts, attendance]);

  const examDayCallout = useMemo(() => {
    const examDays = new Set();
    attempts.forEach((a) => a.attempted_at && examDays.add(a.attempted_at.slice(0, 10)));
    missed.forEach((u) => {
      const day = u.exam_date || u.assigned_at;
      if (day) examDays.add(day.slice(0, 10));
    });
    const absences = attendance.filter((r) => r.status === 'absent');
    const onExamDays = absences.filter((r) => examDays.has(r.date)).length;
    return { total: absences.length, onExamDays };
  }, [attempts, missed, attendance]);

  // Exam attendance: assigned exams the student actually attempted vs. ones
  // they were assigned but never started — same assignment-outward data
  // (attempts / unattempted) that feeds the Absent markers on the score
  // trend graph above. This is distinct from daily attendance below, which
  // stays sourced from the attendance table.
  const examAttendancePieData = useMemo(() => {
    const attemptedCount = attempts.length;
    const missedCount = missed.length;
    return [
      { name: 'Attempted', value: attemptedCount, color: presentColor },
      { name: 'Missed', value: missedCount, color: absentColor },
    ];
  }, [attempts, missed]);

  const homeworkTotal = homeworkSummary.done + homeworkSummary.not_done + homeworkSummary.pending;
  const homeworkCompletionPct = homeworkTotal > 0 ? (homeworkSummary.done / homeworkTotal) * 100 : null;
  const homeworkPieData = useMemo(() => [
    { name: 'Done', value: homeworkSummary.done, color: presentColor },
    { name: 'Not Done', value: homeworkSummary.not_done, color: absentColor },
    { name: 'Pending', value: homeworkSummary.pending, color: pendingColor },
  ], [homeworkSummary]);

  const handleDownload = async (apiFn, filename) => {
    try {
      const res = await apiFn();
      downloadBlob(res.data, filename);
    } catch {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-28 rounded-xl bg-slate-100 dark:bg-slate-700" />
        ))}
      </div>
    );
  }

  if (!data || !student) return null;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{student.name}</h2>
        <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-500 dark:text-slate-400">
          <span>{student.email}</span>
          {student.class_level && <span>Class {student.class_level}</span>}
          {student.board && <span>{student.board}</span>}
          {student.competitive_exam && <span>{student.competitive_exam}</span>}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <SummaryTile
          label="Average Score"
          value={summary.average != null ? `${summary.average.toFixed(1)}%` : '—'}
        />
        <SummaryTile
          label="Homework Done"
          value={homeworkCompletionPct != null ? `${homeworkCompletionPct.toFixed(0)}%` : '—'}
          sub={homeworkTotal > 0 ? `${homeworkSummary.done} of ${homeworkTotal}` : undefined}
        />
        <SummaryTile
          label="Last Exam"
          value={summary.lastPct != null ? `${summary.lastPct.toFixed(1)}%` : '—'}
        />
        <SummaryTile
          label="Trend"
          value={
            summary.trend == null ? '—' : (
              <span className="inline-flex items-center gap-1">
                {summary.trend > 0 ? <TrendingUp size={18} /> : summary.trend < 0 ? <TrendingDown size={18} /> : <Minus size={18} />}
                {Math.abs(summary.trend).toFixed(1)}%
              </span>
            )
          }
          tone={
            summary.trend == null ? undefined
              : summary.trend > 0 ? 'text-emerald-600 dark:text-emerald-400'
              : summary.trend < 0 ? 'text-rose-600 dark:text-rose-400'
              : undefined
          }
          sub={summary.trend != null ? 'vs previous exam' : undefined}
        />
        <SummaryTile
          label="Attendance"
          value={summary.attendancePct != null ? `${summary.attendancePct.toFixed(0)}%` : '—'}
        />
      </div>

      {/* Line graph */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Score Trend</h3>
          {subjects.length > 0 && (
            <select
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
              data-testid="dashboard-subject-filter"
            >
              <option value="all">All Subjects</option>
              {subjects.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>
        {scorePoints.length === 0 && absentPoints.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
            No graded exams yet.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid stroke={gridColor} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="x"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={fmtAxisDate}
                stroke={tickColor}
                tick={{ fontSize: 11, fill: tickColor }}
              />
              <YAxis
                domain={[0, 100]}
                unit="%"
                stroke={tickColor}
                tick={{ fontSize: 11, fill: tickColor }}
                width={44}
              />
              <Tooltip content={<ProgressTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                name="Score %"
                data={scorePoints}
                dataKey="percentage"
                stroke={lineColor}
                strokeWidth={2}
                dot={{ r: 4, fill: lineColor }}
                connectNulls
                isAnimationActive={false}
              />
              <Scatter
                name="Absent"
                data={absentPoints}
                dataKey="percentage"
                fill={absentColor}
                shape="diamond"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Exam attendance pie: attempted vs. assigned-but-missed */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Exam Attendance</h3>
        {attempts.length === 0 && unattempted.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
            No assigned exams yet.
          </p>
        ) : (
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-around">
            <ResponsiveContainer width="100%" height={200} className="sm:max-w-xs">
              <PieChart>
                <Pie
                  data={examAttendancePieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {examAttendancePieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} stroke="none" />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="text-center sm:text-left">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {examDayCallout.total === 0 ? (
                  'No absences recorded.'
                ) : (
                  <>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {examDayCallout.onExamDays} of {examDayCallout.total}
                    </span>{' '}
                    absence{examDayCallout.total === 1 ? '' : 's'} {examDayCallout.total === 1 ? 'was' : 'were'} on exam days
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Homework completion pie: done vs. not_done vs. pending across all class entries */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Homework Completion</h3>
        {homeworkTotal === 0 ? (
          <p className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
            No classroom entries yet.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={200} className="sm:max-w-xs">
            <PieChart>
              <Pie
                data={homeworkPieData}
                dataKey="value"
                nameKey="name"
                innerRadius={45}
                outerRadius={75}
                paddingAngle={2}
                isAnimationActive={false}
              >
                {homeworkPieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} stroke="none" />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Exam history */}
      <div>
        <h3 className="mb-3 text-base font-semibold text-slate-900 dark:text-white">
          {historyLabel} ({attempts.length})
        </h3>
        {attempts.length === 0 ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-12 text-center">
            <FileText size={40} className="mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-slate-500 dark:text-slate-400">{emptyHistoryMessage}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {attempts.map((r) => {
              const cfg = STATUS_CFG[r.status] || STATUS_CFG.not_started;
              const StatusIcon = cfg.icon;
              const pct = r.total_score != null && r.total_marks
                ? ((r.total_score / r.total_marks) * 100).toFixed(1)
                : null;
              return (
                <div
                  key={r.attempt_id}
                  className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 space-y-4"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white">{r.exam_title}</h4>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{r.exam_subject}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{fmtDate(r.attempted_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon size={12} /> {cfg.label}
                      </span>
                      {r.violation_count > 0 && (
                        <span className="rounded-full bg-rose-100 dark:bg-rose-900/30 px-2.5 py-1 text-xs font-semibold text-rose-600 dark:text-rose-400">
                          {r.violation_count} violation{r.violation_count > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>

                  {r.status === 'evaluated' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 p-3 text-center">
                        <p className="text-xs text-slate-500 dark:text-slate-400">Score</p>
                        <p className="text-lg font-bold text-slate-900 dark:text-white">
                          {r.total_score}/{r.total_marks}
                        </p>
                      </div>
                      <div className="rounded-lg bg-primary/10 dark:bg-primary/20 p-3 text-center">
                        <p className="text-xs text-primary dark:text-accent-cool">Percentage</p>
                        <p className="text-lg font-bold text-primary dark:text-accent-cool">
                          {pct != null ? `${pct}%` : '—'}
                        </p>
                      </div>
                    </div>
                  )}

                  {r.feedback && (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 p-3">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 mb-1">Feedback</p>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{r.feedback}</p>
                    </div>
                  )}

                  {(r.submissions.length > 0 || r.corrected_paper_url) && (
                    <div className="flex flex-wrap gap-2">
                      {r.submissions.map((s, i) => (
                        <button
                          key={s.id}
                          onClick={() => handleDownload(
                            () => onDownloadSubmission(s.id),
                            `submission_${i + 1}.pdf`
                          )}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                          <Download size={12} />
                          Upload {r.submissions.length > 1 ? i + 1 : ''}
                          {s.is_late && <span className="ml-1 text-rose-500">(late)</span>}
                        </button>
                      ))}
                      {r.corrected_paper_url && renderCorrectedPaper && renderCorrectedPaper(r)}
                      {r.corrected_paper_url && !renderCorrectedPaper && (
                        <button
                          onClick={() => handleDownload(
                            () => onDownloadCorrected(r.attempt_id),
                            `corrected_${r.exam_title}.pdf`
                          )}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                        >
                          <Download size={12} /> Corrected Paper
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
